import React, { Dispatch, Fragment, FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { DraftDesignConfiguration } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { isCYOMode } from '@/utils/params';
import { usePriceDetails } from '@/hooks/usePriceDetails';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import { getIsDieCutStickerAppearance } from '@/model/productType/getIsDieCutStickerAppearance';
import { isOneSizeProductType } from '@/model/productType/isOneSizeProductType';
import { isSizeAvailable } from '@/model/productType/isSizeAvailable';
import { getSpecialFontCommission } from '@/redux/core/getters/getSpecialFontCommission';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { selectConfigurations } from '@/redux/draft/getters/selectConfigurations';
import { StoreState } from '@/redux/types';
import { LoadingOverlay } from '@/components/common/LoadingOverlay';
import { SizeTable, StickerSizeTable } from '@/components/common/SizeTable';
import { useFocus } from '@/components/common/focus';
import { CaretRight } from '@/components/common/icons/Caret';
import { Ruler } from '@/components/common/icons/Ruler';
import { Panel, PanelContent, PanelHeader, SubPanel } from '@/components/common/panel/panel';
import {
  INLINE_UPSELLING_CONTAINER,
  useUpselling,
} from '@/components/common/sidebar/size-selection/UpsellingModal';
import { setSizeQuantityAction } from '@/components/draft/draftState/actions/setSizeQuantityAction';
import { withoutHistory } from '@/components/draft/draftState/history/withoutHistory';
import { getLocalizedSize } from '@/components/draft/utils/getLocalizedSize';
import { SaveDialog, SaveDialogState, SaveDialogStateAction, useSaveDialog } from '../SaveDialog';
import { AddToBasket } from '../basket/AddToBasket';
import { SizeSelectionPrice } from './SizeSelectionPrice';
import { SizeSelectionPriceDetails } from './SizeSelectionPriceDetails';
import { SizeSelector } from './SizeSelector';
import './SizeSelection.scss';

interface Props {
  saveState: SaveDialogState;
  dispatch: Dispatch<SaveDialogStateAction>;
  onClose: () => void;
}

const StickerSizeSelectionPriceLink: FunctionComponent<{
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
}> = ({ onClick }) => {
  const configuration = useSelector(
    (state: StoreState) => selectConfigurations(state)[0] as DraftDesignConfiguration
  );
  const designSize = getCurrentSizeOfConfiguration(configuration);

  return (
    <Link to="/price/sizes" className="size-selection__chart" onClick={onClick}>
      <Ruler />
      <span>{t('sizePanel.sizeInfoOnesize')}</span>
      {`${getLocalizedSize(designSize.width)} x ${getLocalizedSize(designSize.height)}`}
      <CaretRight />
    </Link>
  );
};

const SizeSelectionPriceLink: FunctionComponent<{
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
  isOneSize: boolean;
}> = ({ onClick, isOneSize }) => {
  return (
    <Link to="/price/sizes" className="size-selection__chart" onClick={onClick}>
      <Ruler />
      <span>{isOneSize ? t('sizePanel.sizeInfoOnesize') : t('sizePanel.sizeInfo')}</span>
      <CaretRight />
    </Link>
  );
};

const SizeSelectionContent: FunctionComponent<Props> = ({ saveState, dispatch, onClose }) => {
  const draftDispatch = useReduxDraftDispatch();
  const appearance = useSelector(getCurrentAppearance);
  const configurations = useSelector(selectConfigurations);

  const [productType, discountBreakdown, selectedSizes, selectedSizesItemCount, totalItemCount] =
    usePriceDetails();

  const isOneSize = isOneSizeProductType(productType);

  const handleSizeQuantityChange = (sizeId: string, quantity: number) => {
    draftDispatch(withoutHistory(setSizeQuantityAction(sizeId, quantity)));
  };

  const handleSizeHelpClick = () => {
    trackFeature('size-selection-size-table');
  };

  const handlePriceDetailsClick = () => {
    trackFeature('size-selection-price-details');
  };

  return (
    <Fragment>
      <PanelHeader
        label={t('modal.close', isOneSize ? t('basket.headingOnesize') : t('basket.chooseSize'))}
        onClose={onClose}
      >
        {isOneSize ? t('basket.headingOnesize') : t('basket.chooseSize')}
      </PanelHeader>
      <PanelContent>
        <div className="size-selection">
          {productType.sizes.map((size) => (
            <SizeSelector
              key={size.id}
              size={size}
              name={isOneSize ? productType.name : size.name}
              currentQuantity={selectedSizes[size.id]}
              available={isSizeAvailable(productType, appearance, size.id)}
              onChange={handleSizeQuantityChange}
            />
          ))}
          {getIsDieCutStickerAppearance(appearance) && configurations.length > 0 ? (
            <StickerSizeSelectionPriceLink onClick={handleSizeHelpClick} />
          ) : (
            <SizeSelectionPriceLink isOneSize={isOneSize} onClick={handleSizeHelpClick} />
          )}
          <div id={INLINE_UPSELLING_CONTAINER} />
        </div>
        <div className="panel__footer">
          <SizeSelectionPrice
            selectedSizesItemCount={selectedSizesItemCount}
            totalItemCount={totalItemCount}
            discountBreakdown={discountBreakdown}
            onDetails={handlePriceDetailsClick}
          />
          <AddToBasket dispatch={dispatch} onAdded={onClose} />
        </div>
        {saveState.loading && <LoadingOverlay />}
        <SaveDialog saveState={saveState} dispatch={dispatch} />
      </PanelContent>
    </Fragment>
  );
};

const PriceDetailsPanelContent: FunctionComponent = () => {
  const appearance = useSelector(getCurrentAppearance);
  const specialFontCommission = useSelector(getSpecialFontCommission);
  const [productType, discountBreakdown, , , totalItemCount, priceBreakdown] = usePriceDetails();

  return (
    <SizeSelectionPriceDetails
      priceBreakdown={priceBreakdown}
      totalItemCount={totalItemCount}
      discountBreakdown={discountBreakdown}
      productType={productType}
      appearance={appearance}
      showPrintingCostTooltip={isCYOMode()}
      specialFontCommission={specialFontCommission}
    />
  );
};

interface SizeSelectionProps {
  active: boolean;
  onClose: () => void;
}

export const SizeSelection: FunctionComponent<SizeSelectionProps> = ({ active, onClose }) => {
  const [saveState, dispatch] = useSaveDialog();

  const [panel, onEntered] = useFocus(active, onClose);

  const navigate = useNavigate();
  const isPriceDetailsActive = Boolean(useMatch('/price/details'));
  const isSizeInfoActive = Boolean(useMatch('/price/sizes'));
  const productType = useSelector(getProductType);
  const appearance = useSelector(getCurrentAppearance);
  const configurations = useSelector(selectConfigurations);
  const upselling = useUpselling(active);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Fragment>
      <Panel
        className="size-selection__panel"
        active={active}
        hidden={isSizeInfoActive || isPriceDetailsActive}
        onEntered={onEntered}
        ref={panel}
      >
        <SizeSelectionContent saveState={saveState} dispatch={dispatch} onClose={onClose} />
      </Panel>
      <SubPanel
        active={isSizeInfoActive}
        headline={productType.name}
        parent={t('basket.chooseSize')}
        onBack={handleBack}
      >
        {getIsDieCutStickerAppearance(appearance) && configurations.length > 0 ? (
          <StickerSizeTable productType={productType} />
        ) : (
          <SizeTable productType={productType} />
        )}
      </SubPanel>
      <SubPanel
        active={isPriceDetailsActive}
        headline={t('basket.priceDetails')}
        parent={t('basket.chooseSize')}
        onBack={handleBack}
      >
        <PriceDetailsPanelContent />
      </SubPanel>
      {upselling}
    </Fragment>
  );
};
